import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import useModalEffect from '../../../hooks/useModalEffect';

const emptyForm = {
  firstName: '',
  lastName: '',
  username: '',
  email: '',
  employeeId: '',
  joinDate: '',
  departmentId: '',
  jobTitleId: '',
  nohp: '',
  address: '',
  emergencyContact: '',
  emergencyAddress: '',
  businessLocation: '',
  country: '',
  state: '',
  city: '',
  postalCode: '',
  status: '1',
};

const toStatusValue = (value) => {
  const normalized = String(value ?? '1').toLowerCase();
  return ['active', '1', 'true'].includes(normalized) ? '1' : '0';
};

const splitName = (employee = {}) => {
  const fullName = String(employee.fullName || employee.name || '').trim();
  const parts = fullName.split(/\s+/).filter(Boolean);
  return {
    firstName: employee.firstName || parts.slice(0, Math.max(1, parts.length - 1)).join(' ') || '',
    lastName: employee.lastName || (parts.length > 1 ? parts[parts.length - 1] : ''),
  };
};

const toDateInputValue = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
};

// Create/Edit Employee — writes through to Datahub (Master Data > Internal
// Users), see hrisApi.js's createEmployee/updateEmployee. Deliberately has
// NO password/role fields — login activation and role assignment happen
// directly in Datahub (md.nexoratech.co), never from this form (explicit
// product decision, 2026-07-27).
const EmployeeFormModal = ({ isOpen, onClose, onSave, employee, departments, jobTitles, saving }) => {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      setError('');
      if (employee) {
        const nameParts = splitName(employee);
        setForm({
          firstName: nameParts.firstName,
          lastName: nameParts.lastName,
          username: employee.username || employee.email || '',
          email: employee.email || '',
          employeeId: employee.employeeId || '',
          joinDate: toDateInputValue(employee.joinDate || employee.createDate),
          departmentId: employee.departmentId || employee.departementId ? String(employee.departmentId || employee.departementId) : '',
          jobTitleId: employee.designationId || employee.jobTitleId ? String(employee.designationId || employee.jobTitleId) : '',
          nohp: employee.nohp || employee.phone || '',
          address: employee.address || '',
          emergencyContact: employee.emergencyContact || '',
          emergencyAddress: employee.emergencyAddress || '',
          businessLocation: employee.businessLocation || '',
          country: employee.country || '',
          state: employee.state || '',
          city: employee.city || '',
          postalCode: employee.postalCode || '',
          status: toStatusValue(employee.status),
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [isOpen, employee]);

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    if (isOpen) document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  useModalEffect(isOpen);

  if (!isOpen) return null;

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const required = employee
    ? ['firstName', 'email', 'departmentId', 'jobTitleId', 'nohp']
    : ['firstName', 'lastName', 'username', 'email', 'employeeId', 'departmentId', 'jobTitleId', 'nohp'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const missing = required.filter((key) => !String(form[key] || '').trim());
    if (missing.length) {
      setError('Lengkapi field yang wajib diisi.');
      return;
    }
    setError('');
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        username: form.username,
        email: form.email,
        employeeId: form.employeeId,
        departmentId: Number(form.departmentId),
        jobTitleId: Number(form.jobTitleId),
        nohp: form.nohp,
        address: form.address,
        emergencyAddress: form.emergencyAddress,
        emergencyContact: form.emergencyContact,
        businessLocation: form.businessLocation,
        country: form.country,
        state: form.state,
        city: form.city,
        postalCode: form.postalCode,
        status: form.status,
      };

      await onSave(payload);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Gagal menyimpan data employee.');
    }
  };

  return ReactDOM.createPortal(
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }} />
      <div className="modal fade show employee-form-modal" style={{ display: 'block', zIndex: 1050 }} tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable employee-form-dialog" role="document">
          <div className="modal-content">
            <form onSubmit={handleSubmit}>
              <div className="modal-header">
                <h5 className="modal-title">{employee ? 'Edit Employee' : 'Add Employee'}</h5>
                <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
              </div>
              <div className="modal-body">
                {error && <div className="alert alert-danger py-2">{error}</div>}
                <div className="mb-2 text-muted small">
                  Aktivasi login (password, role) dilakukan terpisah di <strong>md.nexoratech.co</strong>, bukan dari form ini.
                </div>
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label required">First Name</label>
                    <input className="form-control" value={form.firstName} onChange={set('firstName')} required />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${employee ? '' : 'required'}`}>Last Name</label>
                    <input className="form-control" value={form.lastName} onChange={set('lastName')} required={!employee} />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${employee ? '' : 'required'}`}>Username</label>
                    <input className="form-control" value={form.username} onChange={set('username')} required={!employee} disabled={Boolean(employee)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Email</label>
                    <input type="email" className="form-control" value={form.email} onChange={set('email')} required />
                  </div>
                  <div className="col-md-6">
                    <label className={`form-label ${employee ? '' : 'required'}`}>Employee ID</label>
                    <input className="form-control" value={form.employeeId} onChange={set('employeeId')} required={!employee} disabled={Boolean(employee)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Phone</label>
                    <input className="form-control" value={form.nohp} onChange={set('nohp')} required />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Department</label>
                    <select className="form-select" value={form.departmentId} onChange={set('departmentId')} required>
                      <option value="">Select department...</option>
                      {departments.map((d) => <option key={d.id} value={d.id}>{d.departmentName}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label required">Job Title</label>
                    <select className="form-select" value={form.jobTitleId} onChange={set('jobTitleId')} required>
                      <option value="">Select job title...</option>
                      {jobTitles.map((jt) => <option key={jt.id} value={jt.id}>{jt.title}</option>)}
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Business Location</label>
                    <input className="form-control" value={form.businessLocation} onChange={set('businessLocation')} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Join Date</label>
                    <input type="date" className="form-control" value={form.joinDate} onChange={set('joinDate')} disabled={Boolean(employee)} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Status</label>
                    <select className="form-select" value={form.status} onChange={set('status')}>
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </select>
                  </div>
                  <div className="col-12">
                    <label className="form-label">Address</label>
                    <input className="form-control" value={form.address} onChange={set('address')} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Emergency Contact</label>
                    <input className="form-control" value={form.emergencyContact} onChange={set('emergencyContact')} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Emergency Address</label>
                    <input className="form-control" value={form.emergencyAddress} onChange={set('emergencyAddress')} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">City</label>
                    <input className="form-control" value={form.city} onChange={set('city')} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">State/Province</label>
                    <input className="form-control" value={form.state} onChange={set('state')} />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Postal Code</label>
                    <input className="form-control" value={form.postalCode} onChange={set('postalCode')} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Country</label>
                    <input className="form-control" value={form.country} onChange={set('country')} />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-link link-secondary" onClick={onClose}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : employee ? 'Save Changes' : 'Add Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default EmployeeFormModal;
